/**
 * Pure per-tick decision logic for the deep-link scroll "settle"
 * (operator-task-fe-deep-link-to-comment).
 *
 * When a record is opened cold via a comment permalink (`?comment=<id>`), the
 * deep-linked comment cannot be seated the instant the record mounts: SIBLING
 * content above the comments inside the SAME record scroll container keeps
 * growing for several seconds — the record's value schema-form renders from a
 * short skeleton to a tall form (shoving the comments far down), and its Monaco
 * editors, when they mount, reset the scroller to the top. The record scroll
 * container deliberately runs with `overflow-anchor: none` (Main.scss — it
 * breaks a header-collapse feedback loop, see
 * operator-task-fe-record-header-flap-on-partial-comment-scroll), so the browser
 * does NOT keep the comment visually fixed while that content above it grows.
 *
 * So the deep-link arm does not scroll the comment into view early and then
 * CHASE it as the form keeps displacing it (the chase read as the comment
 * appearing, being pushed back out of view, then slowly re-scrolling — the
 * behaviour Stefan flagged 2026-10-08). Instead it WAITS for the layout to go
 * quiescent and then scrolls the comment into view exactly ONCE. Quiescence is
 * detected by polling the scroll container's content height (`scrollHeight`):
 * once it has stopped changing for `quietMs`, the late value-form growth and the
 * Monaco-editor mounts are done, so a single scroll lands the comment at its
 * final resting place with no visible chasing. A hard cap bounds the wait so it
 * can never run unbounded, and a genuine user scroll gesture abandons the
 * auto-scroll (the user has taken over).
 *
 * The per-tick decision — "height stable this tick? start/extend the quiet
 * timer; else reset it; settle on quiet-window reached or hard-cap elapsed" — is
 * factored out here as a pure function of (state, measured height, now) so it is
 * unit-testable without a live layout, fake DOM, or timers. The owning component
 * (`CommentsPanel.startDeepLinkSettle`) keeps the imperative parts (the poll
 * interval, the gesture listeners, and the single `applyScrollToTarget` + the
 * on-arrival highlight once settled), deliberately ref-owned and NOT tied to the
 * arming effect's cleanup so a React re-render during the long value-form render
 * does not tear the wait down mid-flight.
 */

export interface DeepLinkSettleParams {
    /**
     * How long (ms) the scroll container's content height must stay unchanged
     * before the layout is considered quiescent and the comment is scrolled into
     * view.
     */
    quietMs: number;
    /** Absolute ceiling (ms) from settle start, so it can never run unbounded. */
    hardCapMs: number;
}

export interface DeepLinkSettleState {
    /** Timestamp (ms) the settle began. */
    startMs: number;
    /**
     * Timestamp (ms) the content height most recently STARTED being stable, or
     * null when it is not currently stable (still growing / first tick).
     */
    stableSince: number | null;
    /**
     * The scroll container's content height (`scrollHeight`) observed on the
     * previous tick, or null on the first tick. A change tick-to-tick means the
     * record value-form is still growing (Monaco editors mounting, async markdown
     * resolving), so the comment is NOT yet at its final resting place.
     */
    lastHeight: number | null;
}

export type DeepLinkSettleAction = "continue" | "stop";

export interface DeepLinkSettleTick {
    state: DeepLinkSettleState;
    action: DeepLinkSettleAction;
}

/** Initial settle state, stamped at settle start. */
export const createDeepLinkSettleState = (nowMs: number): DeepLinkSettleState => ({
    startMs: nowMs,
    stableSince: null,
    lastHeight: null,
});

/**
 * Advance the settle one poll tick.
 *
 * @param state   current settle state
 * @param height  the scroll container's current content height (`scrollHeight`)
 * @param nowMs   current time (ms)
 * @param params  timing parameters
 * @returns the next state and whether to keep waiting ("continue") or scroll the
 *          comment into view now ("stop").
 *
 * The settle keys ONLY on content-height stability — not on the comment's
 * current scroll offset — because the comment is deliberately left where it is
 * (un-scrolled) until the layout is done, so its offset is meaningless mid-wait.
 * A longer `quietMs` is needed because growth PAUSES between late Monaco-editor
 * mounts on very tall records: a short window let the wait end inside such a
 * pause, after which the remaining form growth would have stranded the comment
 * far below the fold on the single scroll (observed on a ~13 000px CO with ~10
 * editors). Gating on sustained height-stability closes that gap.
 *
 * Semantics:
 *  - height stable + no prior quiet timer      -> start the quiet timer
 *  - height stable + quiet window elapsed        -> STOP (quiescent -> scroll now)
 *  - height stable + timer not yet elapsed       -> continue
 *  - height changed (still growing)              -> reset the quiet timer
 *  - hard cap elapsed (unless already stopped)   -> STOP (backstop -> scroll now)
 *
 * The first tick (lastHeight === null) can never be "height stable", so the
 * settle always observes at least one height sample before it can stop on quiet.
 */
export const stepDeepLinkSettle = (
    state: DeepLinkSettleState,
    height: number,
    nowMs: number,
    params: DeepLinkSettleParams,
): DeepLinkSettleTick => {
    let stableSince = state.stableSince;
    const heightStable = state.lastHeight !== null && height === state.lastHeight;
    if (heightStable) {
        if (stableSince === null) {
            stableSince = nowMs;
        } else if (nowMs - stableSince >= params.quietMs) {
            return { state: { ...state, stableSince, lastHeight: height }, action: "stop" };
        }
    } else {
        stableSince = null;
    }
    if (nowMs - state.startMs >= params.hardCapMs) {
        return { state: { ...state, stableSince, lastHeight: height }, action: "stop" };
    }
    return { state: { ...state, stableSince, lastHeight: height }, action: "continue" };
};

/**
 * Whether a scroll gesture should abandon the pending auto-scroll. Only genuine,
 * user-originated events qualify: a synthetic event dispatched during layout /
 * Monaco-editor init (isTrusted === false) must NOT abandon the wait, or the
 * comment is never scrolled into view.
 */
export const shouldGestureStopSettle = (isTrusted: boolean): boolean => isTrusted;
